import { useEffect } from "react";
import * as React from "react";
// material-ui
import dayjs from "dayjs";
import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  TextField,
  FormControlLabel,
  Divider,
  DialogActions,
  Switch,
  Box,
} from "@mui/material";

import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// project import
import { openSnackbar } from "../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../api";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SnackbarProps } from "../../../../../types/snackbar";
import TextArea from "antd/es/input/TextArea";
import { FormattedMessage, useIntl } from "react-intl";
import { skillMockdata, levelsOptions } from "./mockData";
import { ResumeSkill } from "../../../../../types/resume";
import { useFormik, FormikProvider, Form } from "formik";

// constant
const getInitialValues = (skill: ResumeSkill | null) => {
  if (skill && skill.id) {
    return {
      skill_name: skill.skill_name || "",
      skill_level: skill.skill_level || "",
      experience_years: skill.experience_years?.toString() || "",
      last_used: skill.last_used || "",
      description: skill.description || "",
      still_used: skill.still_used || false,
    };
  } else {
    return {
      skill_name: skillMockdata.skill_name || "",
      skill_level: skillMockdata.skill_level || "",
      experience_years: skillMockdata.experience_years || "",
      last_used: skillMockdata.last_used || "",
      description: skillMockdata.description || "",
      still_used: false,
    };
  }
};

const SkillSchema = Yup.object().shape({
  skill_name: Yup.string().required("Name is required"),
  skill_level: Yup.string().required("Skill Level is required"),
  experience_years: Yup.string().required("Experience Years Level is required"),
  last_used: Yup.string().required("Last Used is required"),
  description: Yup.string(),
  still_used: Yup.boolean(),
});

function AddEditSkillForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openLanguage: boolean) => void;
  item: ResumeSkill | null;
}) {
  const handleClose = () => {
    setOpen(false);
  };
  const intl = useIntl();

  const formik = useFormik({
    initialValues: getInitialValues(item ?? null),
    validationSchema: SkillSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const skillData = {
          ...values,
          resume_id: resumeId,
          experience_years: values.experience_years,
        };

        if (item && item?.id) {
          const response = await USERAXIOS.post(
            `resume/skills/${item?.id}`,
            skillData
          );
          const updatedSkill = response.data.data;

          if (updatedSkill) {
            setResult((prev) =>
              prev.map((item) =>
                item.id === updatedSkill.id ? updatedSkill : item
              )
            );

            openSnackbar({
              open: true,
              message: "Skill updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post("resume/skills", skillData);
          const newSkill = response.data.data;

          if (newSkill) {
            setResult((prev) => [...prev, newSkill]);

            openSnackbar({
              open: true,
              message: "Skill Added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        openSnackbar({
          open: true,
          message: "Something went wrong",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    handleChange,
  } = formik;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Skill" />
        ) : (
          <FormattedMessage id="Add Skill" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ flex: 1, overflowY: "auto" }}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="personal-skill_name">
                    <FormattedMessage id="skill-name" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="personal-skill_name"
                    name="skill_name"
                    value={values.skill_name}
                    onChange={handleChange}
                    placeholder="Skill Name"
                    error={touched.skill_name && Boolean(errors.skill_name)}
                  />
                  {touched.skill_name && errors.skill_name && (
                    <FormHelperText error>{errors.skill_name}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="personal-reading_level">
                    <FormattedMessage id="level" />
                  </InputLabel>
                  <Select
                    id="reading_level"
                    name="skill_level"
                    value={values.skill_level}
                    onChange={handleChange}
                    error={touched.skill_level && Boolean(errors.skill_level)}
                  >
                    {levelsOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.skill_level && errors.skill_level && (
                    <FormHelperText error>{errors.skill_level}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="personal-experience_years">
                    <FormattedMessage id="years-of-experience" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="personal-experience_years"
                    name="experience_years"
                    value={values.experience_years}
                    onChange={handleChange}
                    placeholder={intl.formatMessage({
                      id: "years-of-experience",
                      defaultMessage: "Years of experience",
                    })}
                    type="number"
                    error={
                      touched.experience_years &&
                      Boolean(errors.experience_years)
                    }
                  />
                  {touched.experience_years && errors.experience_years && (
                    <FormHelperText error>
                      {errors.experience_years}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack direction={"row"}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="last_used">Last Used</InputLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={
                          formik.values.last_used
                            ? dayjs(formik.values.last_used)
                            : null
                        }
                        onChange={(date) => {
                          const formattedDate = date
                            ? date.format("YYYY-MM-DD")
                            : "";
                          formik.setFieldValue("last_used", formattedDate);
                        }}
                      />
                    </LocalizationProvider>
                    {formik.touched.last_used && formik.errors.last_used && (
                      <FormHelperText error>
                        {formik.errors.last_used}
                      </FormHelperText>
                    )}
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="personal-description">
                    Description
                  </InputLabel>
                  <TextArea
                    rows={4}
                    id="personal-description"
                    value={values.description}
                    name="description"
                    onChange={handleChange}
                    placeholder={intl.formatMessage({
                      id: "description",
                      defaultMessage: "Description",
                    })}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.still_used}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "still_used",
                            event.target.checked
                          );
                        }}
                        id="stillWorking-switch"
                      />
                    }
                    label="stillWorking"
                  />
                </Box>
              </Grid>
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditSkillForm;
